import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { brand } from '../../theme';
import StatusChip from '../ui/StatusChip';

/** Where "back" goes: the Library's multicam tab. */
export const MULTICAM_LIBRARY_PATH = '/library?tab=multicam';

interface MulticamStudioHeaderProps {
  title: React.ReactNode;
  status?: string | null;
  /** Shown after the title (length, frame rate). */
  chips?: React.ReactNode;
  /** One line of facts under the title. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * The studio's full-bleed header: a back link to the multicam sets, the
 * eyebrow and status, the title with its chips, a line of facts, and the
 * actions on the right (below the title on phones).
 */
const MulticamStudioHeader: React.FC<MulticamStudioHeaderProps> = ({ title, status, chips, meta, actions }) => (
  <Box
    component="header"
    sx={{ display: 'flex', alignItems: 'flex-start', columnGap: 1.5, rowGap: 1.25, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
  >
    <IconButton
      component={RouterLink}
      to={MULTICAM_LIBRARY_PATH}
      aria-label="Back to multicam sets"
      sx={{ mt: 0.5, border: `1px solid ${brand.border}`, bgcolor: 'background.paper', '&:hover': { bgcolor: brand.ivory } }}
    >
      <ArrowBackOutlinedIcon />
    </IconButton>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.1em', lineHeight: 1.6 }}>
          Multicam studio
        </Typography>
        {status && <StatusChip kind="multicam" status={status} />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 18, md: 21 },
            lineHeight: 1.25,
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        {chips}
      </Box>
      {meta && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25, mt: 0.5, color: 'text.secondary', fontSize: 14 }}>
          {meta}
        </Box>
      )}
    </Box>
    {actions && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          width: { xs: '100%', md: 'auto' },
          flexShrink: 0,
          mt: { md: 0.5 },
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
);

export default MulticamStudioHeader;
